/**
 * useChatConnection — Twilio Conversations hook for WhatsApp chat.
 * Keeps connection alive while the Patient page is mounted. Exposes messages,
 * unreadCount, sendMessage, markAsRead. No hidden DOM; connection lives in the hook.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Client } from "@twilio/conversations";
import {
  getTwilioToken,
  getOrCreateConversation,
  deleteConversation as deleteConversationApi,
  sendInviteTemplate as sendInviteTemplateApi,
  sendWhatsAppMessage as sendWhatsAppMessageApi,
} from "../utils/API_SERVICE_TWILIO";

const RADIOLOGIST_IDENTITY = "radiologist";

export function useChatConnection({
  studyId,
  accessionId,
  accessionNo,
  patientWhatsAppNumber,
  isOpen = false,
} = {}) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | awaitingNumber | loading | ready | error
  const [errorMessage, setErrorMessage] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectedNumber, setConnectedNumber] = useState("");
  const [manualNumber, setManualNumber] = useState("");
  const clientRef = useRef(null);
  const conversationRef = useRef(null);
  const conversationSidRef = useRef(null);
  const mountedRef = useRef(true);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // Track WhatsApp message flow state
  const [hasTemplateSent, setHasTemplateSent] = useState(false);
  const [hasPatientResponded, setHasPatientResponded] = useState(false);

  const caseId = studyId || "";
  const accId = accessionId || accessionNo || "";

  // Helper to get localStorage key for this report
  const getStorageKey = useCallback(() => {
    const reportKey = [caseId, accId].filter(Boolean).join("|") || "default";
    return `twilio_whatsapp_number_${reportKey}`;
  }, [caseId, accId]);

  // Load saved number from localStorage on mount or when report changes
  useEffect(() => {
    if (caseId || accId) {
      try {
        const storageKey = getStorageKey();
        const savedNumber = localStorage.getItem(storageKey);
        if (savedNumber && savedNumber.trim()) {
          setManualNumber(savedNumber.trim());
        }
      } catch (err) {
        console.warn("Failed to load from localStorage:", err);
      }
    }
  }, [caseId, accId, getStorageKey]);

  const markAsRead = useCallback(() => setUnreadCount(0), []);

  const sendMessage = useCallback(
    async (text) => {
      const trimmedText = (text || "").trim();
      if (!trimmedText) {
        return;
      }
      
      if (!connectedNumber) {
        throw new Error("No WhatsApp number connected");
      }
      
      // IMPORTANT: SDK sendMessage doesn't deliver WhatsApp messages
      // It only adds messages to the conversation for UI display
      // For actual WhatsApp delivery, we MUST use Messages API
      
      // Always use Messages API for WhatsApp delivery
      try {
        const result = await sendWhatsAppMessageApi({
          caseId,
          accessionId: accId,
          patientWhatsAppNumber: connectedNumber,
          body: trimmedText,
        });
        
        return result;
      } catch (err) {
        console.error("[useChatConnection] Send message error:", err);
        console.error("[useChatConnection] Error code:", err.code);
        console.error("[useChatConnection] Error message:", err.message);
        
        // Handle 24-hour window expired error
        if (err.code === 63016 || err.code === 63017 || err.requiresTemplate || err.message?.includes("24-hour")) {
          const error = new Error("24-hour window expired. Please send a template message first, or wait for patient to reply.");
          error.code = err.code;
          error.requiresTemplate = true;
          throw error;
        }
        
        throw err;
      }
    },
    [caseId, accId, connectedNumber]
  );

  const clearConnection = useCallback(() => {
    if (conversationRef.current) {
      conversationRef.current.removeAllListeners?.();
    }
    conversationRef.current = null;
    conversationSidRef.current = null;
    setMessages([]);
    setStatus("awaitingNumber");
    setErrorMessage("");
    setConnectedNumber("");
    setManualNumber("");
    setHasTemplateSent(false);
    setHasPatientResponded(false);
    // Clear localStorage for this report
    try {
      localStorage.removeItem(getStorageKey());
    } catch (err) {
      console.warn("Failed to clear localStorage:", err);
    }
  }, [getStorageKey]);

  const changeNumber = useCallback(() => {
    // Clear localStorage before clearing connection
    try {
      localStorage.removeItem(getStorageKey());
    } catch (err) {
      console.warn("Failed to clear localStorage:", err);
    }
    clearConnection();
  }, [clearConnection, getStorageKey]);

  const deleteConversation = useCallback(async () => {
    const number = connectedNumber;
    if (!number) {
      clearConnection();
      return;
    }
    try {
      await deleteConversationApi({
        caseId,
        accessionId: accId,
        patientWhatsAppNumber: number,
      });
    } catch (err) {
      console.error("Delete conversation error:", err);
      throw err;
    }
    // Clear localStorage before clearing connection
    try {
      localStorage.removeItem(getStorageKey());
    } catch (err) {
      console.warn("Failed to clear localStorage:", err);
    }
    clearConnection();
  }, [caseId, accId, connectedNumber, clearConnection, getStorageKey]);

  const connectWithNumber = useCallback((phoneNumber) => {
    if (!phoneNumber || !phoneNumber.trim()) {
      setErrorMessage("chatInvalidPhoneNumber");
      return;
    }
    setManualNumber(phoneNumber.trim());
    setErrorMessage("");
  }, []);

  const sendInvite = useCallback(async ({ contentSid, contentVariables, language = "en" } = {}) => {
    if (!connectedNumber) {
      throw new Error("No connected number");
    }
    try {
      await sendInviteTemplateApi({
        caseId,
        accessionId: accId,
        patientWhatsAppNumber: connectedNumber,
        contentSid,
        contentVariables,
        language,
      });
      // Mark template as sent
      setHasTemplateSent(true);
      // Reset patient response status (waiting for new response)
      setHasPatientResponded(false);
      // Message will appear via SDK messageAdded event
    } catch (err) {
      console.error("Send invite error:", err);
      throw err;
    }
  }, [caseId, accId, connectedNumber]);

  useEffect(() => {
    mountedRef.current = true;
    // Always require manual number entry - ignore prop
    if (!caseId && !accId) {
      setStatus("noCase");
      setErrorMessage("chatNoCase");
      return () => {
        mountedRef.current = false;
      };
    }

    // Always require manual number entry
    if (!manualNumber) {
      setStatus("awaitingNumber");
      return () => {
        mountedRef.current = false;
      };
    }

    let cancelled = false;

    async function init() {
      setStatus("loading");
      setErrorMessage("");
      
      // Helper to check and suppress Twilio SDK errors (defined outside try block for scope)
      const isTwilioSDKError = (error) => {
        const errorStr = error?.message || error?.toString() || '';
        return /Twilsock|request timeout|TM[0-9a-f-]{8,}|SyncError|Connection is not initialized|Unable to connect|_conversationsPromise/i.test(errorStr);
      };
      
      const suppressTwilioError = (error) => {
        if (isTwilioSDKError(error)) {
          // Silently suppress Twilsock timeout errors - they're expected and don't affect functionality
          return true;
        }
        return false;
      };
      
      const handleTwilioError = (err, context = "") => {
        if (isTwilioSDKError(err)) {
          console.warn(`[Twilio SDK] ${context} error suppressed:`, err?.message);
          return true;
        }
        if (mountedRef.current) {
          setStatus("awaitingNumber");
          setErrorMessage(err?.message || "chatError");
        }
        return false;
      };
      
      try {
        const token = await getTwilioToken(RADIOLOGIST_IDENTITY);
        if (cancelled || !mountedRef.current) return;

        // Initialize Twilio Conversations Client (SDK v2.6.5)
        // Client is not fully initialized until 'initialized' event fires
        // Add options to handle timeouts gracefully
        const twilioClient = new Client(token, {
          logLevel: 'warn', // Reduce noise, only show warnings/errors
        });
        clientRef.current = twilioClient;

        // Wrap all event handlers to suppress errors
        const originalEmit = twilioClient.emit?.bind(twilioClient);
        if (originalEmit) {
          twilioClient.emit = function(event, ...args) {
            try {
              return originalEmit(event, ...args);
            } catch (err) {
              if (!suppressTwilioError(err)) {
                throw err;
              }
            }
          };
        }

        twilioClient.on("connectionStateChanged", (state) => {
          if (state === "disconnected" || state === "denied") {
            console.warn("[Twilio] Connection state:", state);
            // Don't treat disconnection as fatal - it may reconnect
          }
        });

        twilioClient.on("tokenExpired", () => {
          console.warn("[Twilio] Token expired - reconnecting");
        });

        // Catch any unhandled errors from the client
        twilioClient.on("error", (error) => {
          if (suppressTwilioError(error)) {
            // Timeout errors are expected and handled gracefully
            return;
          }
          // Only log non-suppressed errors
          console.error("[Twilio] Unhandled error:", error);
        });

        twilioClient.on("initialized", async () => {
          if (cancelled || !mountedRef.current) return;
          try {
            const conversationSid = await getOrCreateConversation({
              caseId,
              accessionId: accId,
              patientWhatsAppNumber: manualNumber,
            });
            if (cancelled || !mountedRef.current) return;

            const conv = await twilioClient.getConversationBySid(conversationSid);
            conversationRef.current = conv;
            conversationSidRef.current = conversationSid;
            
            // Ensure conversation is joined (required for SDK to work properly)
            try {
              await conv.join();
            } catch (joinErr) {
              // Ignore "already joined" errors
            }
            
            // Brief sync wait (backend may have just added participant)
            await new Promise(resolve => setTimeout(resolve, 800));
            
            // Quick participant check – don't block UI; backend may return 200 without WhatsApp (50416)
            const getMessagingBinding = (p) => {
              if (p.messagingBinding) return p.messagingBinding;
              if (p.messaging_binding) return p.messaging_binding;
              if (p.attributes) {
                try {
                  const attrs = typeof p.attributes === 'string' ? JSON.parse(p.attributes) : p.attributes;
                  return attrs?.messagingBinding || attrs?.messaging_binding || null;
                } catch (e) {
                  return null;
                }
              }
              return null;
            };
            
            let participants = [];
            try {
              participants = await conv.getParticipants();
            } catch (partErr) {
              console.warn("[useChatConnection] getParticipants failed:", partErr?.message);
            }
            
            const hasWhatsApp = participants.some(p => {
              if (p.type === 'whatsapp') return true;
              const binding = getMessagingBinding(p);
              return binding?.address && binding.address.includes('whatsapp:');
            });
            if (!hasWhatsApp) {
              console.warn("[useChatConnection] No WhatsApp participant in conversation. Messages may not deliver to phone.");
            }

            const paginator = await conv.getMessages();
            const list = (paginator.items || []).map((m) => {
              const isFromRadiologist = (m.author || "").toLowerCase() === RADIOLOGIST_IDENTITY.toLowerCase();
              let isTemplate = false;
              try {
                const attrs = typeof m.attributes === 'string' ? JSON.parse(m.attributes) : m.attributes || {};
                isTemplate = attrs.isTemplate === true;
              } catch (e) {
                // Ignore parse errors
              }
              return {
                sid: m.sid,
                body: m.body || "",
                author: m.author || "",
                dateCreated: m.dateCreated,
                isFromRadiologist,
                isTemplate,
              };
            });
            setMessages(list);
            
            // Check if template has been sent and patient has responded
            const hasTemplate = list.some(m => m.isFromRadiologist && m.isTemplate);
            const hasResponse = list.some(m => !m.isFromRadiologist);
            setHasTemplateSent(hasTemplate);
            setHasPatientResponded(hasResponse);
            
            setStatus("ready");
            // Track connected number
            const trimmedNumber = manualNumber.trim();
            setConnectedNumber(trimmedNumber);
            // Save to localStorage for this report
            try {
              const storageKey = getStorageKey();
              localStorage.setItem(storageKey, trimmedNumber);
            } catch (err) {
              console.warn("Failed to save to localStorage:", err);
            }

            conv.on("messageAdded", (message) => {
              try {
                if (!mountedRef.current) return;
                const isFromRadiologist =
                  (message.author || "").toLowerCase() === RADIOLOGIST_IDENTITY.toLowerCase();
                if (!isFromRadiologist && !isOpenRef.current) {
                  setUnreadCount((c) => c + 1);
                }
                
                // Check if message is a template
                let isTemplate = false;
                try {
                  const attrs = typeof message.attributes === 'string' ? JSON.parse(message.attributes) : message.attributes || {};
                  isTemplate = attrs.isTemplate === true;
                } catch (e) {
                  // Ignore parse errors
                }
                
                setMessages((prev) => {
                  // Skip if message already exists (by SID)
                  if (prev.some(m => m.sid === message.sid)) {
                    return prev;
                  }
                  
                  // Remove temporary optimistic messages with same content
                  const filtered = prev.filter(m => 
                    !(m.sid?.startsWith('temp_') && 
                      m.body === message.body && 
                      m.isFromRadiologist && 
                      isFromRadiologist)
                  );
                  
                  const newMessage = {
                    sid: message.sid,
                    body: message.body || "",
                    author: message.author || "",
                    dateCreated: message.dateCreated,
                    isFromRadiologist,
                    isTemplate,
                  };
                  
                  // Update template/response tracking
                  if (isFromRadiologist && isTemplate) {
                    setHasTemplateSent(true);
                    setHasPatientResponded(false);
                  } else if (!isFromRadiologist) {
                    setHasPatientResponded(true);
                  }
                  
                  return [...filtered, newMessage];
                });
              } catch (msgErr) {
                if (!suppressTwilioError(msgErr)) {
                  console.error("[Twilio] Message handler error:", msgErr);
                }
              }
            });
          } catch (err) {
            handleTwilioError(err, "Conversation");
          }
        });

        twilioClient.on("initFailed", ({ error }) => {
          handleTwilioError(error, "Connection");
        });
      } catch (err) {
        handleTwilioError(err, "Init");
      }
    }

    init();
    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (conversationRef.current) {
        conversationRef.current.removeAllListeners?.();
      }
      if (clientRef.current) {
        clientRef.current.shutdown?.();
      }
      clientRef.current = null;
      conversationRef.current = null;
      conversationSidRef.current = null;
    };
  }, [caseId, accId, manualNumber, getStorageKey]);

  const hasConversation = status === "ready";

  // Check if free-form messages are allowed
  const canSendFreeFormMessage = hasTemplateSent && hasPatientResponded;

  return {
    messages,
    status,
    errorMessage,
    sendMessage,
    unreadCount,
    markAsRead,
    hasConversation,
    connectedNumber,
    changeNumber,
    deleteConversation,
    connectWithNumber,
    sendInvite,
    hasTemplateSent,
    hasPatientResponded,
    canSendFreeFormMessage,
  };
}
